import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Checkbox, InlineNotification, Modal, NumberInput, Select, SelectItem, Toggle } from '@carbon/react';
import { RISK_ACTION_LABEL, type RiskAction } from '@ruby-cms/shared';
import { api, ApiError } from '../api/client';
import { BlockSkeleton, ErrorState, InlineError, PageHeader } from '../components/states';
import { When } from '../components/format';
import { CommandStatus, HE_A_ERRORS } from '../components/labels';
import type { Command } from './Commands';

type Mode = 'cho_phep' | 'chan' | 'xem_xet';
type Limit = { toiDa?: number; ngay?: number; cheDo?: Mode };
type Rule = { bat?: boolean; hanhDong?: RiskAction[] } & Record<string, unknown>;
type Doc = {
  lienKet?: { thietBi?: boolean; thietBiCungIp?: boolean; soTk?: boolean; chuTk?: boolean; ip?: boolean; ngay?: number };
  dangKy?: { thietBiNgay?: Limit; ipNgay?: Limit };
  dangNhap?: { taiKhoanMoiThietBi?: Limit };
  thuong?: { cheDo?: Mode };
  haiCua?: { cheDo?: Mode; capLienKet?: { cheDo?: Mode }; game?: Record<string, [number | string, number | string][]>; tenCua?: Record<string, Record<string, string>> };
  nhipCuoc?: { cuocMoiGiay?: number; chotTruocMs?: number };
  quyTac?: Record<string, Rule>;
};
type Data = { noiDung: Doc; macDinh: Doc; meta: { version: number; source: string; at: string | null }; pending: { id: string; status: string }[] };

const MODES: [Mode, string][] = [['chan', 'Chặn'], ['xem_xet', 'Cho qua, mở mục xem xét (cảnh báo khi duyệt rút)'], ['cho_phep', 'Tắt (cho phép)']];
const ACTIONS: RiskAction[] = ['giu_rut', 'co', 'bao'];
// tham so cua tung quy tac (backend lib/chong_lam_dung.js QUY_TAC): [khoa, nhan, buoc]
const RULES: { key: string; label: string; help: string; params: [string, string, number][] }[] = [
  { key: 'thangLon', label: 'Thắng lớn', help: 'Một lần trả thưởng từ số tiền này trở lên.', params: [['tien', 'Số tiền trả', 1_000_000]] },
  { key: 'chuoiThang', label: 'Chuỗi thắng', help: 'Thắng liên tiếp số ván này trong một game.', params: [['soVan', 'Số ván thắng liên tiếp', 1]] },
  { key: 'tyLeThang', label: 'Tỷ lệ thắng bất thường', help: 'Tiền trả của các ván gần nhất vượt kỳ vọng theo RTP của game quá số độ lệch chuẩn này.',
    params: [['sigma', 'Số sigma', 0.5], ['soVan', 'Xét số ván gần nhất', 10], ['soVanToiThieu', 'Cần ít nhất số ván', 10]] },
  { key: 'napRutItChoi', label: 'Nạp rồi rút mà chơi ít', help: 'Rút khi tiền cược từ lần nạp gần nhất nhỏ hơn hệ số này nhân tiền nạp.', params: [['tyLeCuoc', 'Hệ số cược / nạp', 0.1]] },
  { key: 'saiDangNhap', label: 'Sai mật khẩu nhiều lần', help: 'Số lần sai mật khẩu trong số phút.', params: [['soLan', 'Số lần', 1], ['phut', 'Trong số phút', 5]] },
  { key: 'thietBiMoi', label: 'Rút từ thiết bị mới', help: 'Rút tiền từ thiết bị mới xuất hiện trong số giờ, khi tài khoản đã dùng thiết bị khác trước đó.', params: [['gio', 'Thiết bị mới trong số giờ', 1]] },
  { key: 'congCu', label: 'Dấu hiệu công cụ cược', help: 'Trong các cược gần nhất: phần lớn đặt ở giây cuối, hoặc thời điểm đặt đều bất thường.',
    params: [['soCuocXet', 'Xét số cược gần nhất', 5], ['giayCuoi', 'Giây cuối', 0.5], ['tyLeGiayCuoi', 'Tỷ lệ cược ở giây cuối (0-1)', 0.05], ['doLechMs', 'Độ lệch dưới (ms)', 50]] },
  { key: 'cuocQuaNhanh', label: 'Đặt cược quá nhanh', help: 'Bị từ chối vì vượt số lệnh mỗi giây từ số lần này trong một phút.', params: [['soLan', 'Số lần bị từ chối', 1]] },
  { key: 'daiLy', label: 'Đại lý chung thiết bị / ngân hàng với thành viên', help: 'Thành viên dùng chung thiết bị hoặc tài khoản ngân hàng với tài khoản game của đại lý.', params: [] },
];
const GAME_NAME: Record<string, string> = { vgmn_100: 'Tài Xỉu', vgmn_101: 'Tài Xỉu MD5', vgmn_103: 'Xóc Đĩa', '3980': 'Sicbo', '3974': 'Baccarat', '5996': 'Rồng Hổ', vgkn_1003: 'Keno' };

const isStale = (e: unknown) => e instanceof ApiError && e.status === 409 && e.code === 'STALE';

function ModeSelect({ id, label, v, on }: { id: string; label: string; v: Mode | undefined; on: (m: Mode) => void }) {
  return (
    <Select id={id} labelText={label} value={v ?? 'cho_phep'} onChange={(e) => on(e.target.value as Mode)}>
      {MODES.map(([m, t]) => <SelectItem key={m} value={m} text={t} />)}
    </Select>
  );
}

function Num({ id, label, v, step = 1, min = 0, on, help }: { id: string; label: string; v: unknown; step?: number; min?: number; on: (n: number) => void; help?: string }) {
  return <NumberInput id={id} label={label} helperText={help} min={min} step={step} value={Number(v ?? 0)} allowEmpty={false}
    onChange={(_e, { value }) => { const n = Number(value); if (Number.isFinite(n)) on(n); }} />;
}

// Anti-abuse settings (roadmap P1-D20): one he A config document 'chong_lam_dung', saved by a signed CONFIG command.
export function RiskConfigPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['risk-config'], queryFn: () => api.get<Data>('/api/admin/risk/config') });
  const [draft, setDraft] = useState<{ doc: Doc; version: number } | null>(null);
  const [open, setOpen] = useState(false);
  const [notice, setNotice] = useState<{ kind: 'success' | 'warning'; title: string; subtitle: string } | null>(null);
  const live: Doc = draft?.doc ?? q.data?.noiDung ?? {};
  const put = (fn: (d: Doc) => Doc) => setDraft({ doc: fn(structuredClone(live)), version: draft?.version ?? q.data?.meta.version ?? 0 });
  const reload = () => {
    setDraft(null); setOpen(false); qc.invalidateQueries({ queryKey: ['risk-config'] });
    setNotice({ kind: 'warning', title: 'Cài đặt vừa được người khác lưu', subtitle: 'Thay đổi của bạn chưa được lưu. Trang đã tải lại bản mới nhất, hãy sửa lại.' });
  };
  const save = useMutation({
    mutationFn: () => api.put<{ command: Command }>('/api/admin/risk/config', { noiDung: live, expect: draft?.version ?? q.data?.meta.version ?? 0 }),
    onSuccess: (r) => {
      const c = r.command;
      if (c.status === 'FAILED' && c.lastError === 'PHIEN_BAN_CU') return reload();
      setDraft(null); setOpen(false); qc.invalidateQueries({ queryKey: ['risk-config'] });
      setNotice(c.status === 'SUCCEEDED'
        ? { kind: 'success', title: 'Hệ game đã nhận cài đặt mới', subtitle: 'Áp dụng ngay cho lần đăng ký, đăng nhập, nhận thưởng, cược và rút tiếp theo.' }
        : { kind: 'warning', title: 'Lệnh chưa xong', subtitle: (c.lastError && HE_A_ERRORS[c.lastError]) || c.lastError || c.status });
    },
    onError: (e) => { if (isStale(e)) reload(); },
  });

  const lk = live.lienKet ?? {};
  const rule = (k: string): Rule => live.quyTac?.[k] ?? {};
  const setRule = (k: string, p: Partial<Rule>) => put((d) => ({ ...d, quyTac: { ...(d.quyTac ?? {}), [k]: { ...(d.quyTac?.[k] ?? {}), ...p } } }));

  return (
    <div className="page">
      <PageHeader title="Chống gian lận"
        lead="Giới hạn theo thiết bị / IP / tài khoản ngân hàng, cược hai cửa, nhịp cược và các quy tắc rủi ro tự động. Hệ game áp dụng ngay nơi người chơi thao tác; mục mở ra nằm ở Hàng chờ rủi ro." />
      {q.isLoading ? <BlockSkeleton lines={10} /> : q.error ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : (
        <>
          <p className="muted">Phiên bản {q.data?.meta.version} ({q.data?.meta.source === 'cms' ? 'CMS' : 'mặc định'}){q.data?.meta.at ? <> · <When v={q.data.meta.at} /></> : null}</p>
          {notice ? <InlineNotification kind={notice.kind} lowContrast title={notice.title} subtitle={notice.subtitle} onClose={() => setNotice(null)} /> : null}
          {q.data?.pending.length ? <p>Có lệnh đang chờ: {q.data.pending.map((p) => <CommandStatus key={p.id} s={p.status} />)}</p> : null}

          <section className="section">
            <h2>Hai tài khoản liên kết khi</h2>
            <div className="check-grid">
              <Checkbox id="lk-tb" labelText="Chung thiết bị" checked={lk.thietBi !== false} onChange={(_e, { checked }) => put((d) => ({ ...d, lienKet: { ...d.lienKet, thietBi: checked } }))} />
              <Checkbox id="lk-tbip" labelText="Thiết bị tính cùng IP" checked={lk.thietBiCungIp !== false} onChange={(_e, { checked }) => put((d) => ({ ...d, lienKet: { ...d.lienKet, thietBiCungIp: checked } }))} />
              <Checkbox id="lk-stk" labelText="Chung số tài khoản ngân hàng" checked={lk.soTk !== false} onChange={(_e, { checked }) => put((d) => ({ ...d, lienKet: { ...d.lienKet, soTk: checked } }))} />
              <Checkbox id="lk-ctk" labelText="Chung tên chủ tài khoản" checked={lk.chuTk !== false} onChange={(_e, { checked }) => put((d) => ({ ...d, lienKet: { ...d.lienKet, chuTk: checked } }))} />
              <Checkbox id="lk-ip" labelText="Chung IP (nhiều người chung mạng)" checked={!!lk.ip} onChange={(_e, { checked }) => put((d) => ({ ...d, lienKet: { ...d.lienKet, ip: checked } }))} />
            </div>
            <p className="muted">Vân tay trình duyệt trùng giữa các điện thoại cùng đời: ghép với IP để không liên kết người lạ.</p>
            <Num id="lk-ngay" label="Chỉ xét đăng nhập trong số ngày" v={lk.ngay} min={1} on={(n) => put((d) => ({ ...d, lienKet: { ...d.lienKet, ngay: n } }))} />
          </section>

          <section className="section">
            <h2>Đăng ký và đăng nhập</h2>
            <div className="control__group"><div className="row">
              <Num id="dk-tb" label="Tài khoản mới / thiết bị / 24 giờ" v={live.dangKy?.thietBiNgay?.toiDa} min={1} on={(n) => put((d) => ({ ...d, dangKy: { ...d.dangKy, thietBiNgay: { ...d.dangKy?.thietBiNgay, toiDa: n } } }))} />
              <ModeSelect id="dk-tb-m" label="Vượt thì" v={live.dangKy?.thietBiNgay?.cheDo} on={(m) => put((d) => ({ ...d, dangKy: { ...d.dangKy, thietBiNgay: { ...d.dangKy?.thietBiNgay, cheDo: m } } }))} />
            </div><div className="row">
              <Num id="dk-ip" label="Tài khoản mới / IP / 24 giờ" v={live.dangKy?.ipNgay?.toiDa} min={1} on={(n) => put((d) => ({ ...d, dangKy: { ...d.dangKy, ipNgay: { ...d.dangKy?.ipNgay, toiDa: n } } }))} />
              <ModeSelect id="dk-ip-m" label="Vượt thì" v={live.dangKy?.ipNgay?.cheDo} on={(m) => put((d) => ({ ...d, dangKy: { ...d.dangKy, ipNgay: { ...d.dangKy?.ipNgay, cheDo: m } } }))} />
            </div><div className="row">
              <Num id="dn-tb" label="Tài khoản khác đã vào cùng thiết bị" v={live.dangNhap?.taiKhoanMoiThietBi?.toiDa} min={1} on={(n) => put((d) => ({ ...d, dangNhap: { taiKhoanMoiThietBi: { ...d.dangNhap?.taiKhoanMoiThietBi, toiDa: n } } }))} />
              <Num id="dn-ngay" label="Trong số ngày" v={live.dangNhap?.taiKhoanMoiThietBi?.ngay} min={1} on={(n) => put((d) => ({ ...d, dangNhap: { taiKhoanMoiThietBi: { ...d.dangNhap?.taiKhoanMoiThietBi, ngay: n } } }))} />
              <ModeSelect id="dn-m" label="Đạt thì" v={live.dangNhap?.taiKhoanMoiThietBi?.cheDo} on={(m) => put((d) => ({ ...d, dangNhap: { taiKhoanMoiThietBi: { ...d.dangNhap?.taiKhoanMoiThietBi, cheDo: m } } }))} />
            </div></div>
          </section>

          <section className="section">
            <h2>Thưởng tân thủ</h2>
            <ModeSelect id="th-m" label="Giftcode và khuyến mãi nạp lần đầu: lần thứ hai trên cùng thiết bị / tài khoản ngân hàng" v={live.thuong?.cheDo} on={(m) => put((d) => ({ ...d, thuong: { cheDo: m } }))} />
          </section>

          <section className="section">
            <h2>Cược hai cửa đối nhau</h2>
            <div className="control__group"><div className="row">
              <ModeSelect id="hc-m" label="Một tài khoản đặt cửa đối (trong một ván)" v={live.haiCua?.cheDo} on={(m) => put((d) => ({ ...d, haiCua: { ...d.haiCua, cheDo: m } }))} />
              <ModeSelect id="hc-cap" label="Hai tài khoản liên kết đặt hai cửa đối" v={live.haiCua?.capLienKet?.cheDo} on={(m) => put((d) => ({ ...d, haiCua: { ...d.haiCua, capLienKet: { cheDo: m } } }))} />
            </div></div>
            <p className="muted">Mọi chế độ đều loại phần cược ghép được khỏi vòng cược, hoa hồng đại lý và hoàn trả. Cặp cửa: {Object.entries(live.haiCua?.game ?? {}).map(([g, ds]) => `${GAME_NAME[g] ?? g} ${ds.map((c) => c.map((x) => live.haiCua?.tenCua?.[g]?.[String(x)] ?? x).join('/')).join(', ')}`).join(' · ')}</p>
          </section>

          <section className="section">
            <h2>Nhịp cược</h2>
            <div className="control__group"><div className="row">
              <Num id="nc-giay" label="Lệnh cược tối đa mỗi giây (0 = tắt)" v={live.nhipCuoc?.cuocMoiGiay} on={(n) => put((d) => ({ ...d, nhipCuoc: { ...d.nhipCuoc, cuocMoiGiay: n } }))} />
              <Num id="nc-chot" label="Ngừng nhận cược trước khi hết giờ (ms, đồng hồ máy chủ)" v={live.nhipCuoc?.chotTruocMs} step={100} on={(n) => put((d) => ({ ...d, nhipCuoc: { ...d.nhipCuoc, chotTruocMs: n } }))} />
            </div></div>
          </section>

          <section className="section">
            <h2>Quy tắc rủi ro tự động</h2>
            <div className="stack">
              {RULES.map((r) => {
                const cur = rule(r.key);
                const acts = cur.hanhDong ?? [];
                return (
                  <div key={r.key} className="stack-tight risk-rule">
                    <Toggle id={'qt-' + r.key} labelText={r.label} labelA="Tắt" labelB="Bật" toggled={cur.bat !== false} onToggle={(v) => setRule(r.key, { bat: v })} />
                    <p className="muted">{r.help}</p>
                    {r.params.length ? (
                      <div className="control__group"><div className="row">
                        {r.params.map(([k, label, step]) => <Num key={k} id={`qt-${r.key}-${k}`} label={label} v={cur[k]} step={step} on={(n) => setRule(r.key, { [k]: n })} />)}
                      </div></div>
                    ) : null}
                    <div className="row">
                      {ACTIONS.map((a) => (
                        <Checkbox key={a} id={`qt-${r.key}-${a}`} labelText={RISK_ACTION_LABEL[a]} checked={acts.includes(a)}
                          onChange={(_e, { checked }) => setRule(r.key, { hanhDong: checked ? [...acts, a] : acts.filter((x) => x !== a) })} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <div className="row section">
            <Button disabled={!draft} onClick={() => setOpen(true)}>Lưu</Button>
            {draft ? <Button kind="ghost" onClick={() => setDraft(null)}>Bỏ thay đổi</Button> : null}
          </div>
          <Modal open={open} modalHeading="Lưu cài đặt chống gian lận?" primaryButtonText="Lưu" secondaryButtonText="Hủy"
            primaryButtonDisabled={save.isPending} onRequestClose={() => setOpen(false)} onRequestSubmit={() => save.mutate()}>
            <div className="stack">
              <p>Hệ game áp dụng ngay cho lần đăng ký, đăng nhập, nhận thưởng, cược và rút tiếp theo. Mục rủi ro đã mở giữ nguyên.</p>
              <InlineError error={save.error} />
            </div>
          </Modal>
        </>
      )}
    </div>
  );
}
